import getI18N from '../../lib/i18n';
import { getConfig } from '../config';
import { FormFields, TRouteType, Coords } from '../types';
import { escapeHtml } from '../utils';

const DEFAULT_KEY = 'qEukSFZtlsFYPatLKyvqYpzAWGaclFec';
const DEFAULT_CLID_API = 'ak230315';
const DEFAULT_COORDS: Coords = { lat: '55.733842', lon: '37.588144' };

type UrlParams =
    | 'app'
    | 'start-lat'
    | 'start-lon'
    | 'end-lat'
    | 'end-lon'
    | 'tariff'
    | 'ref'
    | 'redirect'
    | 'lang'
    | 'erid'
    | 'data-app';

type RequiredWidgetAttributes = 'ref' | 'proxy-url' | 'tariff' | 'app' | 'lang' | 'redirect';

type LocationAttributes = 'point-a' | 'point-b' | 'use-location';

type OptionalWidgetAttributes =
    | 'size'
    | 'theme'
    | 'title'
    | 'description'
    | 'tariff'
    | 'picture'
    | 'custom-layout'
    | 'clid'
    | 'apikey'
    | 'erid'
    | 'data-app'
    | LocationAttributes;

type WidgetConfig = Record<RequiredWidgetAttributes, string> &
    Partial<Record<OptionalWidgetAttributes, string>>;

const setDataAttr = (elem: HTMLElement) => (attrName: OptionalWidgetAttributes, value: string) => {
    elem.setAttribute(`data-${attrName}`, value);
};

const i18n = getI18N();

const getLocationConfig = (
    coords: FormFields['addressCoords'],
    routeType: TRouteType = 'to'
): Pick<WidgetConfig, LocationAttributes> => {
    const config = { 'use-location': 'true', 'point-a': '', 'point-b': '' };

    const normalizedCoords = `${coords?.lon || DEFAULT_COORDS.lon},${
        coords?.lat || DEFAULT_COORDS.lat
    }`;

    switch (routeType) {
        case 'from':
            return {
                ...config,
                'use-location': 'false',
                'point-a': normalizedCoords,
            };
        case 'to':
            return {
                ...config,
                'point-b': normalizedCoords,
            };
        case 'none':
        default:
            return config;
    }
};

const getWidgetConfig = (fields: FormFields): WidgetConfig => {
    const appConfig = getConfig(fields.app);
    const config: WidgetConfig = {
        ref: encodeURIComponent(fields.clid),
        'proxy-url': appConfig.proxyUrl,
        tariff: fields.tariff,
        app: fields.app,
        lang: fields.lang,
        ...(fields.erid && { erid: escapeHtml(fields.erid) }),
        redirect: appConfig.redirectCode,
    };

    const { type: widgetType } = fields;

    switch (widgetType) {
        case 'badge':
            return {
                ...config,
                size: 'xs',
                theme: fields.style,
                title: escapeHtml(i18n(fields.titleFixed)),
                ...getLocationConfig(fields.addressCoords, fields.routeType),
            };

        case 'button':
            if (fields.subtitle === 'address') {
                config.description = escapeHtml(fields.addressShort);
            }
            if (fields.subtitle === 'price') {
                config.clid = escapeHtml(fields.priceClid) || DEFAULT_CLID_API;
                config.apikey = escapeHtml(fields.priceKey) || DEFAULT_KEY;
            }
            return {
                ...config,
                size: 's',
                theme: fields.style,
                title: escapeHtml(fields.title),
                ...getLocationConfig(fields.addressCoords),
            };

        case 'card':
            if (fields.image) {
                config.picture = fields.image;
            }
            if (fields.isPriceShown) {
                config.clid = escapeHtml(fields.priceClid) || DEFAULT_CLID_API;
                config.apikey = escapeHtml(fields.priceKey) || DEFAULT_KEY;
            }
            return {
                ...config,
                size: 'm',
                theme: 'normal',
                title: escapeHtml(fields.title),
                ...getLocationConfig(fields.addressCoords),
            };

        case 'qr':
        case 'link':
            if (fields.routeType !== 'none' && fields.isPriceShown) {
                config.clid = escapeHtml(fields.priceClid);
                config.apikey = escapeHtml(fields.priceKey);
            }
            return {
                ...config,
                'custom-layout': 'true',
                title: escapeHtml(fields.title),
                ...getLocationConfig(fields.addressCoords, fields.routeType),
            };

        default:
            return config;
    }
};

const mapUrlParamToDataAttr = (param: UrlParams, config: WidgetConfig) => {
    switch (param) {
        case 'start-lat':
            return config['point-a']?.split(',')[1] || '';
        case 'start-lon':
            return config['point-a']?.split(',')[0] || '';
        case 'end-lat':
            return config['point-b']?.split(',')[1] || '';
        case 'end-lon':
            return config['point-b']?.split(',')[0] || '';
        case 'tariff':
            return config.tariff;
        default:
            return config[param];
    }
};

const getWidgetFromConfig = (config: WidgetConfig, innerContent = ''): string => {
    const widgetEl = document.createElement('div');
    widgetEl.classList.add('ya-taxi-widget');
    const setWidgetAttr = setDataAttr(widgetEl);
    Object.entries(config).forEach(([attr, value]) =>
        setWidgetAttr(attr as OptionalWidgetAttributes, value)
    );
    widgetEl.innerHTML = innerContent;
    const scriptStr = `<script src="https://yastatic.net/taxi-widget/ya-taxi-widget-v2.js"></script>`;
    const widgetString = `${scriptStr}${widgetEl.outerHTML}`;
    return widgetString;
};

const getUrlFromWidgetConfig = (config: WidgetConfig) => {
    const { proxyUrl } = getConfig(config.app);
    const search = new URL(proxyUrl).search.slice(1);
    const params = search.split('&').map((pair) => pair.split('='));
    const mappedParams = params
        .map(([key, val]) => [key, mapUrlParamToDataAttr(val.slice(1, -1) as UrlParams, config)])
        .filter(([, val]) => val);
    return proxyUrl
        .replace('{app}', config.app)
        .replace(search, `${mappedParams.map((param) => param.join('=')).join('&')}`);
};

export const getGeneratedCode = (fields: FormFields): { widget: string; url: string } => {
    const config: WidgetConfig = getWidgetConfig(fields);

    const url = getUrlFromWidgetConfig(config);

    let widgetString = '';

    switch (fields.type) {
        case 'qr':
            break;
        case 'link': {
            if (!fields.isPriceShown) {
                const a = document.createElement('a');
                a.setAttribute('href', url);
                a.textContent = escapeHtml(fields.title);
                widgetString = a.outerHTML;
                break;
            }
            const descrEl = '<div data-description="true"></div>';
            const linkEl = `<div><a href="#" data-link="true">${escapeHtml(
                fields.title
            )}</a></div>`;
            const disclaimerEl = '<div data-disclaimer="true"></div>';
            const divContent = `${descrEl}${linkEl}${disclaimerEl}`;
            widgetString = getWidgetFromConfig(config, divContent);
            break;
        }
        default: {
            widgetString = getWidgetFromConfig(config);
            break;
        }
    }
    return { widget: widgetString, url };
};
